/**
 * Quem tem acesso a quê, e quem está esperando.
 *
 * ## O que esta tela não mostra, e diz que não mostra
 *
 * Acesso concedido por **grupo do Entra** não passa por tabela nossa: ele é
 * decidido no instante em que a pessoa abre o portfólio, a partir do token.
 * Listá-lo exigiria consultar o Graph item por item, e o portal do Entra já
 * faz isso melhor.
 *
 * O aviso no rodapé existe para ninguém concluir que esta lista é o retrato
 * completo — a conclusão errada aqui seria "fulano não tem acesso", quando ele
 * tem pelo diretório.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, SessionError, goToSignIn } from '../api.js';
import { notify } from './Toast.jsx';

/** Estado → como aparece. A ordem da lista põe pendente primeiro. */
const STATES = {
  PENDING: { label: 'Solicitado', cls: 'pending' },
  GRANTED: { label: 'Liberado', cls: 'ok' },
  REJECTED: { label: 'Recusado', cls: 'neutral' },
  REVOKED: { label: 'Retirado', cls: 'neutral' },
};

/** De onde veio a concessão. Só faz sentido para quem tem. */
const SOURCES = {
  SIMULATED: 'simulação',
  MANUAL: 'concedido à mão',
  SERVICENOW: 'ServiceNow',
};

function dateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AdminAccess() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const [filters, setFilters] = useState({ userOid: '', itemId: '', state: '' });
  const [novo, setNovo] = useState({ userOid: '', itemId: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.admin.access());
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      setLoading(false);
    }
    return undefined;
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const rows = data?.access ?? [];
    return rows.filter(
      (r) =>
        (!filters.userOid || r.userOid === filters.userOid) &&
        (!filters.itemId || r.itemId === filters.itemId) &&
        (!filters.state || r.state === filters.state),
    );
  }, [data, filters]);

  const set = (campo) => (e) => setFilters((f) => ({ ...f, [campo]: e.target.value }));

  async function conceder(userOid, itemId) {
    setBusy(`${userOid}:${itemId}`);
    try {
      await api.admin.grantAccess(userOid, itemId);
      notify('Acesso liberado. A pessoa foi avisada.');
      await load();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message);
    } finally {
      setBusy(null);
    }
    return undefined;
  }

  async function retirar(userOid, itemId) {
    setBusy(`${userOid}:${itemId}`);
    try {
      await api.admin.revokeAccess(userOid, itemId);
      notify('Acesso retirado. A pessoa foi avisada.');
      await load();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message);
    } finally {
      setBusy(null);
    }
    return undefined;
  }

  const users = data?.users ?? [];
  const items = data?.items ?? [];
  const pendentes = (data?.access ?? []).filter((r) => r.state === 'PENDING').length;
  const liberados = (data?.access ?? []).filter((r) => r.state === 'GRANTED').length;

  return (
    <>
      <div className="stats-bar">
        <div className="stat">
          <div className="stat-label">Acessos liberados</div>
          <div className="stat-value">{data ? liberados : '—'}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Aguardando</div>
          <div className="stat-value">{data ? pendentes : '—'}</div>
        </div>
      </div>

      {/* Conceder direto, sem esperar pedido. É o caso que não cabe em grupo:
          alguém que precisa hoje, de um item que não tem grupo no Entra. */}
      <div className="list-panel">
        <div className="panel-hd">
          <h2>Liberar acesso</h2>
        </div>

        <div className="filter-row">
          <div className="field field-grow">
            <label className="field-label" htmlFor="g-user">
              Usuário
            </label>
            <select
              id="g-user"
              className="input"
              value={novo.userOid}
              onChange={(e) => setNovo((n) => ({ ...n, userOid: e.target.value }))}
            >
              <option value="">Escolha…</option>
              {users.map((u) => (
                <option key={u.oid} value={u.oid}>
                  {u.displayName ?? u.upn}
                </option>
              ))}
            </select>
          </div>

          <div className="field field-grow">
            <label className="field-label" htmlFor="g-item">
              Item
            </label>
            <select
              id="g-item"
              className="input"
              value={novo.itemId}
              onChange={(e) => setNovo((n) => ({ ...n, itemId: e.target.value }))}
            >
              <option value="">Escolha…</option>
              {items.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field field-check">
            <button
              type="button"
              className="button primary"
              disabled={!novo.userOid || !novo.itemId || busy !== null}
              onClick={async () => {
                await conceder(novo.userOid, novo.itemId);
                setNovo({ userOid: '', itemId: '' });
              }}
            >
              Liberar
            </button>
          </div>
        </div>
      </div>

      <div className="list-panel">
        <div className="filter-row">
          <div className="field field-grow">
            <label className="field-label" htmlFor="f-user">
              Usuário
            </label>
            <select id="f-user" className="input" value={filters.userOid} onChange={set('userOid')}>
              <option value="">Todos</option>
              {users.map((u) => (
                <option key={u.oid} value={u.oid}>
                  {u.displayName ?? u.upn}
                </option>
              ))}
            </select>
          </div>

          <div className="field field-grow">
            <label className="field-label" htmlFor="f-item2">
              Item
            </label>
            <select id="f-item2" className="input" value={filters.itemId} onChange={set('itemId')}>
              <option value="">Todos</option>
              {items.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field-label" htmlFor="f-state">
              Situação
            </label>
            <select id="f-state" className="input" value={filters.state} onChange={set('state')}>
              <option value="">Todas</option>
              {Object.entries(STATES).map(([id, s]) => (
                <option key={id} value={id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading && <p className="state-message">Carregando…</p>}

        {error && (
          <div className="state-message error" role="alert">
            <p>{error}</p>
            <button type="button" onClick={load}>
              Tentar de novo
            </button>
          </div>
        )}

        {!loading && !error && visible.length === 0 && (
          <p className="state-message">
            Nenhum acesso registrado com esses filtros. Quem tem acesso por grupo do Entra não
            aparece aqui — veja o aviso abaixo.
          </p>
        )}

        {!loading && !error && visible.length > 0 && (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Usuário</th>
                  <th scope="col">Item</th>
                  <th scope="col">Situação</th>
                  <th scope="col">Desde</th>
                  <th scope="col" className="col-action">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const s = STATES[r.state] ?? { label: r.state, cls: 'neutral' };
                  const trabalhando = busy === `${r.userOid}:${r.itemId}`;

                  return (
                    <tr key={`${r.userOid}:${r.itemId}`}>
                      <th scope="row" className="cell-person">
                        <span className="person-text">
                          <span className="person-name">{r.userName ?? '—'}</span>
                          <span className="person-upn">{r.userUpn ?? r.userOid}</span>
                        </span>
                      </th>

                      <td>
                        <span className="cell-main">{r.itemName}</span>
                        <span className="cell-sub">
                          {r.itemKind === 'AGENT' ? 'Agente' : 'Sistema'}
                        </span>
                      </td>

                      <td>
                        <span className={`status-pill ${s.cls}`}>{s.label}</span>
                        {r.state === 'GRANTED' && r.source && (
                          <span className="cell-sub">{SOURCES[r.source] ?? r.source}</span>
                        )}
                        {r.state === 'PENDING' && r.justification && (
                          <span className="cell-sub">“{r.justification}”</span>
                        )}
                      </td>

                      <td>
                        <span className="cell-main">{dateTime(r.at)}</span>
                      </td>

                      <td className="col-action">
                        {r.state === 'GRANTED' ? (
                          <button
                            type="button"
                            className="button ghost row-action"
                            disabled={trabalhando}
                            onClick={() => retirar(r.userOid, r.itemId)}
                          >
                            {trabalhando ? '…' : 'Retirar'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="button ghost row-action"
                            disabled={trabalhando}
                            onClick={() => conceder(r.userOid, r.itemId)}
                          >
                            {trabalhando ? '…' : 'Liberar'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="panel-ft">
          Esta lista mostra o acesso que a <b>plataforma</b> concedeu — por solicitação, à mão, ou
          pela simulação. Quem tem acesso por <b>grupo do Entra</b> não aparece aqui: esse acesso é
          decidido no token, a cada abertura de tela, e quem o lista é o portal do Entra.
          {data?.simulation && (
            <>
              {' '}
              <b>A simulação está ligada:</b> toda solicitação é aprovada sozinha, sem aprovador e
              sem chamado.
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default AdminAccess;
